import * as React from "react";
import { useHistory } from "react-router";
import {
  useSocialCoachSession,
  URLS,
  printBasicDate,
  useAccountSettings,
  useDebounced,
  exportAssistantList,
} from "@/utils";
import { useEffect, useState, useRef } from "react";
import { AssistantCoachServices, AssistantCoach, AssistantCoachFilterParams } from "@/services/bedrock";
import { UserStatus } from "@socialcoach/services";
import { Combobox, Button, InputGroup, Input, Badge, Table, Spinner, ContactCell, Skeleton } from "@/components/ui";
import { Search } from "lucide-react";
import { CoachComponents, WebComponents } from "@/interfaces";
import { toast } from "sonner";

const AssistantsFC: React.FC = () => {
  const history = useHistory();
  const { coachId } = useSocialCoachSession();
  const { hasWritePermission, hasAccessToCoachComponent } = useAccountSettings();
  const withWritePermission = hasWritePermission(WebComponents.ASSISTANTS);

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounced(searchTerm, 500);

  const statusOptions = [
    { key: "ALL", label: "All", value: "ALL" },
    { key: "Active", label: "Active", value: "ACTIVE" },
    { key: "Cancelled", label: "Cancelled", value: "SUSPENDED" },
    { key: "Pending", label: "Pending", value: "PENDING" },
  ];

  const [selectedStatus, setSelectedStatus] = useState(statusOptions[0]);

  const [assistants, setAssistants] = useState<AssistantCoach[]>([]);
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [exporting, setExporting] = useState<boolean>(false);

  const observerRef = useRef<HTMLDivElement | null>(null);

  const fetchData = async (pageNum: number, isLoadMore: boolean) => {
    setError(false);
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const filterParams: AssistantCoachFilterParams = {
        page: pageNum,
        perPage: 20,
        sort: "user_id",
        order: "desc",
        extraFields: ["regions", "branches", "explicitPlayerCount"],
      };

      if (debouncedSearchTerm.trim()) {
        filterParams.search = debouncedSearchTerm.trim();
      }

      if (selectedStatus.value !== "ALL") {
        filterParams.status = selectedStatus.value as UserStatus;
      }

      const response = await AssistantCoachServices.getAssistantCoaches(filterParams);
      const newItems = response.content || [];
      const totalPages = response.pagination?.totalPages || 1;

      if (isLoadMore) {
        setAssistants(prev => [...prev, ...newItems]);
      } else {
        setAssistants(newItems);
      }

      setHasMore(pageNum < totalPages);
      setPage(pageNum);
    } catch (e) {
      console.error("Error fetching assistant coaches:", e);
      setError(true);
      if (isLoadMore) {
        setHasMore(false);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchData(1, false);
  }, [debouncedSearchTerm, selectedStatus, coachId]);

  useEffect(() => {
    if (!hasMore || loading || loadingMore || error) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting && hasMore && !loading && !loadingMore && !error) {
          fetchData(page + 1, true);
        }
      },
      { threshold: 0, rootMargin: "100px" }
    );

    const currentSentinel = observerRef.current;
    if (currentSentinel) {
      observer.observe(currentSentinel);
    }

    return () => {
      if (currentSentinel) {
        observer.unobserve(currentSentinel);
      }
    };
  }, [hasMore, loading, loadingMore, error, page, debouncedSearchTerm, selectedStatus]);

  const exportAssistants = async () => {
    if (exporting) return;
    setExporting(true);
    let percentComplete = 0;
    const toastId = toast.loading(`Exporting... (${percentComplete}%)`);
    let exportPage = 1;
    let hasMorePages = true;
    const allResults: AssistantCoach[] = [];
    try {
      while (hasMorePages) {
        const filterParams: AssistantCoachFilterParams = {
          page: exportPage,
          perPage: 100,
          sort: "user_id",
          order: "desc",
          extraFields: ["regions", "branches", "explicitPlayerCount", "explicitPlayerEmails", "componentPermissions"],
        };
        if (searchTerm.trim()) {
          filterParams.search = searchTerm.trim();
        }
        if (selectedStatus.value !== "ALL") {
          filterParams.status = selectedStatus.value as UserStatus;
        }

        const nextResults = await AssistantCoachServices.getAssistantCoaches(filterParams);
        const totalPages = nextResults.pagination?.totalPages || 1;
        allResults.push(...(nextResults.content || []));
        hasMorePages = totalPages > exportPage;
        percentComplete = (exportPage / totalPages) * 100;
        toast.loading(`Exporting... (${percentComplete.toFixed(0)}%)`, { id: toastId });
        exportPage++;
      }
    } catch (e) {
      console.error("Error exporting assistant coaches:", e);
      toast.error("Export failed. Please try again.", { id: toastId });
      return;
    } finally {
      setExporting(false);
    }
    if (allResults.length === 0) {
      toast.info("There are no assistants to export.", { id: toastId });
      return;
    }
    toast.success("Export complete.", { id: toastId });
    const components = Object.values(CoachComponents).filter(c => hasAccessToCoachComponent(c.id));
    exportAssistantList(allResults, components);
  };

  const getSegmentNames = (assistant: AssistantCoach) => {
    const segments = [
      ...(assistant.regions || []).map(r => ({ ...r, type: "REGION" })),
      ...(assistant.branches || []).map(b => ({ ...b, type: "BRANCH" })),
    ].sort((a, b) => b.type.localeCompare(a.type) || (a.name || "").localeCompare(b.name || ""));

    const hasSegments = segments.length > 0;
    const hasPlayers = assistant.explicitPlayerCount !== undefined && assistant.explicitPlayerCount > 0;

    if (hasSegments || hasPlayers)
      return (
        <div className="flex flex-row flex-wrap gap-1">
          {segments.map(s => (
            <Badge
              key={`segmentBadge-${s.type}-${s.name}`}
              size={"small"}
              variant={"outline"}
              className={s.type === "REGION" ? "bg-pink-100" : "bg-blue-100"}
            >
              <div className="font-medium text-[10px]">{s.name}</div>
            </Badge>
          ))}
          {hasPlayers && (
            <Badge size={"small"} variant={"outline"} className="bg-green-100">
              <div className="font-medium text-[10px]">
                +{assistant.explicitPlayerCount} {assistant.explicitPlayerCount === 1 ? "Player" : "Players"}
              </div>
            </Badge>
          )}
        </div>
      );
    else return "";
  };

  const getStatusLabel = (status?: string) => {
    if (!status) return "";
    if (status.toUpperCase() === "SUSPENDED") return "Cancelled";
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4 flex-wrap">
          <InputGroup
            className="w-80"
            control={controlProps => (
              <Input
                {...controlProps}
                placeholder="Search Assistant..."
                onChange={e => setSearchTerm(e.target.value)}
              />
            )}
            append={<Search className="w-4 h-4 text-muted-foreground" />}
          />

          <Combobox
            className="w-50"
            items={statusOptions}
            value={selectedStatus}
            onValueChange={data => setSelectedStatus(data ?? statusOptions[0])}
          />
          {loading && !loadingMore && <Spinner className="w-4 h-4" />}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" children="Export" onClick={exportAssistants} disabled={exporting} />
          {withWritePermission && (
            <Button onClick={() => history.push(URLS.coach.newAssistant)} children="Add Assistant" />
          )}
        </div>
      </div>

      <Table>
        <Table.Header>
          <Table.Row>
            <Table.Head>Assistant</Table.Head>
            <Table.Head>Status</Table.Head>
            <Table.Head>
              <div className={"flex flex-row gap-1"}>
                <Badge size={"small"} variant={"outline"} className={"bg-pink-100"}>
                  Regions
                </Badge>
                <div>/</div>
                <Badge size={"small"} variant={"outline"} className={"bg-blue-100"}>
                  Branches
                </Badge>
              </div>
            </Table.Head>
            <Table.Head>Added</Table.Head>
            <Table.Head>Last Login</Table.Head>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {error && assistants.length === 0 ? (
            <Table.Row>
              <Table.Cell colSpan={5} className="h-24 text-center">
                <div className="flex flex-col items-center justify-center gap-2 py-4">
                  <div className="font-semibold text-destructive">Failed to load Assistants</div>
                  <div className="text-sm text-muted-foreground">
                    There was an error loading the data. Please check your connection and try again.
                  </div>
                  <Button variant="outline" size="sm" className="mt-2" onClick={() => fetchData(1, false)}>
                    Retry
                  </Button>
                </div>
              </Table.Cell>
            </Table.Row>
          ) : loading && assistants.length === 0 ? (
            Array.from({ length: 5 }).map((_, i) => (
              <Table.Row key={`skeletonRow-${i}`}>
                <Table.Cell>
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-9 w-9 rounded-full shrink-0" />
                    <div className="flex flex-col gap-1.5 flex-1">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-3 w-48" />
                    </div>
                  </div>
                </Table.Cell>
                <Table.Cell>
                  <Skeleton className="h-5 w-16" />
                </Table.Cell>
                <Table.Cell>
                  <div className="flex gap-1">
                    <Skeleton className="h-5 w-20" />
                  </div>
                </Table.Cell>
                <Table.Cell>
                  <Skeleton className="h-4 w-24" />
                </Table.Cell>
                <Table.Cell>
                  <Skeleton className="h-4 w-24" />
                </Table.Cell>
              </Table.Row>
            ))
          ) : assistants.length > 0 ? (
            assistants.map(assistant => {
              const goToAssistantDetail = () => {
                const assistantUrl = URLS.coach.editAssistant.replace(":userId", `${assistant.userId}`);
                history.push(assistantUrl);
              };
              return (
                <Table.Row
                  key={`assistantRow-${assistant.userId}`}
                  className="cursor-pointer"
                  onClick={goToAssistantDetail}
                >
                  <Table.Cell>
                    <div className="flex items-center gap-2">
                      <ContactCell
                        showAvatar
                        name={assistant.fullName || `${assistant.firstName} ${assistant.lastName}`}
                        email={assistant.email}
                        imageUrl={assistant.photoUrl}
                      />
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    <div className="capitalize">{getStatusLabel(assistant.status)}</div>
                  </Table.Cell>
                  <Table.Cell>{getSegmentNames(assistant)}</Table.Cell>
                  <Table.Cell className="text-muted-foreground">
                    {assistant.createdDate && printBasicDate(assistant.createdDate)}
                  </Table.Cell>
                  <Table.Cell className="text-muted-foreground">
                    {assistant.lastLoginDate && printBasicDate(assistant.lastLoginDate)}
                  </Table.Cell>
                </Table.Row>
              );
            })
          ) : (
            !loading && (
              <Table.Row>
                <Table.Cell colSpan={5} className="h-24 text-center">
                  <div className="flex flex-col items-center justify-center gap-1">
                    <Search className="w-8 h-8 text-muted-foreground/50" />
                    <div className="font-semibold">No results</div>
                    <div className="text-sm text-muted-foreground">There are no assistants in this section</div>
                  </div>
                </Table.Cell>
              </Table.Row>
            )
          )}
        </Table.Body>
      </Table>

      {loadingMore && (
        <div className="flex items-center justify-center p-4">
          <Spinner className="w-6 h-6" />
        </div>
      )}

      <div ref={observerRef} className="h-4 w-full" />
    </div>
  );
};

export const Assistants = AssistantsFC;
